import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { List } from 'immutable';

import { convertDateString } from 'src/utils/dateFormat';
import { deleteEmailTemplateSendGrid } from 'src/actions/emailTemplatesSendGrid';
import { downloadHtml } from 'src/utils/downloadFile';

import AgGridTable from '../../helpers/AgGridTable';
import { usePopupContext } from '../../../contexts/PopupContext';
import { IconButton } from '../../helpers/IconButton';
import Button from '../../helpers/Button';

import style from './style.module.css';

const ACTIONS_FIELD = 'actions';
const gridStyle = { height: '100%' };
const sortingOrder = ['asc', 'desc'];

const formatDate = ({ value }) => (value ? convertDateString(value) : '');

/**
 * Cell renderer of the "Actions" column.
 *
 * @param {object} props Props.
 * @param {object} props.data Row data.
 * @param {boolean} props.isLoading Disables the buttons while templates are loading.
 * @param {Function} props.onEdit Handle edit click, called with the template id.
 * @param {Function} props.onDelete Handle delete click, called with the template id.
 */
const ActionsCellRendererComponent = ({ data, isLoading, onEdit, onDelete }) => {
  const handleEditClick = useCallback(() => onEdit(data.id), [data.id, onEdit]);

  const handleDownloadSourceClick = useCallback(() => downloadHtml(data.htmlContent, data.templateName), [
    data.htmlContent,
    data.templateName,
  ]);

  const handleDeleteClick = useCallback(() => onDelete(data.id), [data.id, onDelete]);

  return (
    <div className={style.buttons}>
      <Button disabled={isLoading} onClick={handleEditClick}>
        Edit
      </Button>
      <Button className="btn-default hide" disabled={isLoading} onClick={handleDownloadSourceClick}>
        Download Source
      </Button>
      <IconButton
        disabled={isLoading}
        icon="trash"
        onClick={handleDeleteClick}
        size="sm"
        title="Delete"
        variant="danger"
      />
    </div>
  );
};

ActionsCellRendererComponent.propTypes = {
  data: PropTypes.shape({
    htmlContent: PropTypes.string,
    id: PropTypes.number.isRequired,
    templateName: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const ActionsCellRenderer = memo(ActionsCellRendererComponent);

const COLUMNS = [
  { headerName: 'Activity', field: 'activity', flex: 60, minWidth: 60 },
  { headerName: 'Buyer', field: 'buyerName', flex: 160, minWidth: 160 },
  { headerName: 'Project', field: 'projectName', flex: 160, minWidth: 160 },
  { headerName: 'Name', field: 'templateName', flex: 180, minWidth: 180 },
  { headerName: 'Created', field: 'createdAt', flex: 80, minWidth: 80, valueFormatter: formatDate },
  { headerName: 'Created By', field: 'creatorName', flex: 80, minWidth: 80 },
  { headerName: 'Update', field: 'updateAt', flex: 80, minWidth: 80, valueFormatter: formatDate },
  { headerName: 'Update By', field: 'updaterName', flex: 80, minWidth: 80 },
];

const EmailTemplateTableComponent = ({ isLoading, emailTemplates, onSortChange, deleteEmailTemplateSendGrid }) => {
  const { openPopup, closePopup } = usePopupContext();

  const handleEdit = useCallback(
    id => {
      const url = new URL(window.location.href);

      url.searchParams.set('id', id);
      window.history.pushState({}, '', url);

      openPopup('EditEmailTemplateSendGridPopup', { idEmailTemplate: id });
    },
    [openPopup],
  );

  const handleDelete = useCallback(
    id => {
      openPopup('DeleteConfirmPopup', {
        delete: () => {
          deleteEmailTemplateSendGrid(id);
          closePopup();
        },
        title: 'Delete template',
        message: 'Are you sure you want to delete this template?',
      });
    },
    [openPopup, closePopup, deleteEmailTemplateSendGrid],
  );

  const columnDefs = useMemo(
    () =>
      List([
        ...COLUMNS.map(column => ({ ...column, sortingOrder, suppressSizeToFit: true })),
        {
          headerName: 'Actions',
          field: ACTIONS_FIELD,
          width: 120,
          suppressSizeToFit: true,
          sortable: false,
          cellRendererFramework: ActionsCellRenderer,
          cellRendererParams: { isLoading, onEdit: handleEdit, onDelete: handleDelete },
        },
      ]),
    [isLoading, handleEdit, handleDelete],
  );

  const handleSortModelChanged = useCallback(
    sortModel => {
      const [sortedColumn] = sortModel;

      if (!sortedColumn) return;

      onSortChange(sortedColumn.colId);
    },
    [onSortChange],
  );

  return (
    <div className={style.host}>
      <AgGridTable
        columnDefs={columnDefs}
        isFetching={isLoading}
        onSortModelChanged={handleSortModelChanged}
        rowData={emailTemplates}
        style={gridStyle}
        serverSideSorting
        sortable
      />
    </div>
  );
};

EmailTemplateTableComponent.propTypes = {
  deleteEmailTemplateSendGrid: PropTypes.func.isRequired,
  emailTemplates: PropTypes.instanceOf(List).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

/** Email template table component. */
export const EmailTemplateTable = memo(
  connect(null, {
    deleteEmailTemplateSendGrid,
  })(EmailTemplateTableComponent),
);
